import { Download, FileArchive, Terminal, Cloud, GitBranch } from 'lucide-react';

import { ReviewPanel } from '../ReviewPanel';
import { Button } from '@/components/ui/button';
import type { ArtifactRow } from '@/hooks/useJobProgress';

interface DeliveryPanelProps {
  artifacts: ArtifactRow[];
  /** Optional — embed differently when used standalone vs in pipeline tab. */
  variant?: 'panel' | 'standalone';
}

export function DeliveryPanel({ artifacts, variant = 'panel' }: DeliveryPanelProps) {
  // A page can have several renders (one per LUMEN revision); show only the
  // latest per page label, in first-seen order.
  const hifiByRef = new Map<string, ArtifactRow>();
  for (const a of artifacts) {
    if (a.kind === 'hifi_page_image') hifiByRef.set(a.ref ?? a.filename, a);
  }
  const hifiPages = Array.from(hifiByRef.values());
  // Likewise the zip: a retried FORGE stage uploads a fresh archive.
  const codeZip = [...artifacts].reverse().find((a) => a.kind === 'code_zip');

  const body = (
    <div className="space-y-8">
      <section className="space-y-3">
        <p className="smallcaps text-graphite">High-fidelity mockups</p>
        {hifiPages.length === 0 ? (
          <p className="text-graphite">No mockups produced yet.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {hifiPages.map((a) => (
              <figure key={a._id} className="space-y-1.5">
                <div className="overflow-hidden rounded-sm border border-ink/10 bg-paper-2">
                  {a.url ? (
                    <img src={a.url} alt={a.ref ?? a.filename} className="block w-full" />
                  ) : (
                    <div className="aspect-video grid place-items-center text-graphite text-[12px]">
                      Image not yet available
                    </div>
                  )}
                </div>
                <figcaption className="font-mono text-[11px] text-ink">
                  {a.ref ?? a.filename}
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <p className="smallcaps text-graphite">Deployable code</p>
        {codeZip ? (
          <div className="flex items-start gap-4 rounded-sm border border-ink/10 bg-paper-2 p-4">
            <FileArchive className="h-8 w-8 text-ochre flex-shrink-0" aria-hidden />
            <div className="flex-1 min-w-0">
              <p className="font-mono text-[12px] text-ink">{codeZip.filename}</p>
              <p className="font-mono text-[11px] text-graphite">
                {humanBytes(codeZip.bytes)} · ready to download
              </p>
              <p className="text-[12px] text-graphite mt-1">
                A Vite + React + TypeScript + Tailwind project — instructions below.
              </p>
            </div>
            {codeZip.url ? (
              <Button asChild variant="accent">
                <a href={codeZip.url} download={codeZip.filename}>
                  <Download className="mr-2 h-3.5 w-3.5" aria-hidden /> Download
                </a>
              </Button>
            ) : null}
          </div>
        ) : (
          <p className="text-graphite">Code zip not yet ready.</p>
        )}
      </section>

      {codeZip ? <DeploymentInstructions /> : null}
    </div>
  );

  if (variant === 'standalone') return body;

  return (
    <ReviewPanel agent="forge" index={8} subtitle="Delivery">
      {body}
    </ReviewPanel>
  );
}

function humanBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

// ─────────────────────────────────────────────────────────────────────────
//  Deployment instructions
// ─────────────────────────────────────────────────────────────────────────

/**
 * Hardcoded step-by-step instructions for running the downloaded zip
 * locally and deploying it to Vercel. The same steps apply to every
 * project AURA emits — Vite + React + TS + Tailwind — so we render them
 * statically rather than synthesising per-project copy.
 */
function DeploymentInstructions() {
  return (
    <section className="space-y-6">
      <p className="smallcaps text-graphite">After you download</p>

      <div className="grid gap-6 md:grid-cols-2">
        <DeployCard
          icon={<Terminal className="h-5 w-5 text-ochre" aria-hidden />}
          title="Run it locally"
          subtitle="Get the site on your machine in about a minute."
          steps={[
            <>
              <strong>Extract the zip.</strong> Right-click the downloaded file and choose
              &ldquo;Extract All…&rdquo; (Windows) or double-click (macOS). You&rsquo;ll get a folder
              with the project source.
            </>,
            <>
              <strong>Install Node.js</strong> if you don&rsquo;t have it.{' '}
              <a
                className="underline decoration-ink/30 underline-offset-2 hover:decoration-ochre hover:text-ochre"
                href="https://nodejs.org/en/download"
                target="_blank"
                rel="noopener noreferrer"
              >
                nodejs.org/en/download
              </a>{' '}
              — pick the LTS installer.
            </>,
            <>
              <strong>Open the folder</strong> in VS Code or Cursor (File → Open Folder…).
            </>,
            <>
              <strong>Open the integrated terminal</strong> (View → Terminal, or
              <CodeKey>Ctrl</CodeKey>+<CodeKey>`</CodeKey>) and install dependencies:
              <Code block>npm install</Code>
            </>,
            <>
              <strong>Start the dev server</strong>:
              <Code block>npm run dev</Code>
              Open the printed URL (usually <Code>http://localhost:5173</Code>) in your browser.
            </>,
            <>
              <strong>To build for production</strong>, run <Code>npm run build</Code> — output
              lands in <Code>dist/</Code>.
            </>,
          ]}
        />

        <DeployCard
          icon={<Cloud className="h-5 w-5 text-ochre" aria-hidden />}
          title="Deploy to Vercel"
          subtitle="Free hosting at a real URL with HTTPS, in five minutes."
          steps={[
            <>
              <strong>Sign in to GitHub</strong> at{' '}
              <a
                className="underline decoration-ink/30 underline-offset-2 hover:decoration-ochre hover:text-ochre"
                href="https://github.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                github.com
              </a>
              . If you don&rsquo;t have an account, create one — it&rsquo;s free.
            </>,
            <>
              <strong>Create a new repository</strong> (
              <a
                className="underline decoration-ink/30 underline-offset-2 hover:decoration-ochre hover:text-ochre"
                href="https://github.com/new"
                target="_blank"
                rel="noopener noreferrer"
              >
                github.com/new
              </a>
              ). Pick a name; leave it empty (no README, no .gitignore — the project already has
              them).
            </>,
            <>
              <strong>From the project folder</strong>, in a terminal, push your code:
              <Code block>{
`git init
git add .
git commit -m "AURA-generated initial commit"
git branch -M main
git remote add origin https://github.com/<your-username>/<your-repo>.git
git push -u origin main`}</Code>
              <span className="text-graphite text-[11px]">
                Replace <Code>{`<your-username>`}</Code> and <Code>{`<your-repo>`}</Code> with
                the values from the GitHub repo page.
              </span>
            </>,
            <>
              <strong>Sign in to Vercel</strong> at{' '}
              <a
                className="underline decoration-ink/30 underline-offset-2 hover:decoration-ochre hover:text-ochre"
                href="https://vercel.com/login"
                target="_blank"
                rel="noopener noreferrer"
              >
                vercel.com/login
              </a>{' '}
              with the same GitHub account.
            </>,
            <>
              <strong>Click &ldquo;Add New… → Project&rdquo;</strong>, pick the repo you just pushed,
              and accept Vercel&rsquo;s defaults — it auto-detects Vite, sets{' '}
              <Code>npm run build</Code> as the build command and <Code>dist</Code> as the output
              directory.
            </>,
            <>
              <strong>Click &ldquo;Deploy&rdquo;</strong>. After ~1 minute you&rsquo;ll get a live URL
              like <Code>your-project.vercel.app</Code>. Every subsequent <Code>git push</Code>{' '}
              redeploys automatically.
            </>,
          ]}
        />
      </div>

      <p className="rounded-sm border border-ink/10 bg-paper-2/50 px-4 py-3 text-[12px] text-graphite flex items-start gap-3">
        <GitBranch className="mt-0.5 h-4 w-4 text-ochre flex-shrink-0" aria-hidden />
        <span>
          <strong className="text-ink">Tip.</strong> The generated project already includes a{' '}
          <Code>.gitignore</Code> that excludes <Code>node_modules/</Code> and{' '}
          <Code>dist/</Code>. You only commit source files — Vercel installs and builds on its own
          servers.
        </span>
      </p>
    </section>
  );
}

interface DeployCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  steps: React.ReactNode[];
}

function DeployCard({ icon, title, subtitle, steps }: DeployCardProps) {
  return (
    <div className="rounded-sm border border-ink/10 bg-paper-2/50 p-5 space-y-4">
      <header className="flex items-center gap-2">
        {icon}
        <div>
          <h3 className="font-serif text-display-xs italic text-ink leading-tight">{title}</h3>
          <p className="text-[12px] text-graphite">{subtitle}</p>
        </div>
      </header>
      <ol className="space-y-3 text-[13px] text-ink">
        {steps.map((step, i) => (
          <li key={i} className="flex gap-3">
            <span
              className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-ink/25 font-mono text-[10px] text-ink"
              aria-hidden
            >
              {i + 1}
            </span>
            <div className="space-y-2 leading-relaxed">{step}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Code({
  children,
  block = false,
}: {
  children: React.ReactNode;
  block?: boolean;
}) {
  if (block) {
    return (
      <pre className="my-2 overflow-x-auto rounded-sm border border-ink/10 bg-ink/5 p-3 font-mono text-[11px] text-ink leading-relaxed whitespace-pre">
        {children}
      </pre>
    );
  }
  return (
    <code className="rounded-sm bg-ink/5 px-1.5 py-0.5 font-mono text-[11px] text-ink">
      {children}
    </code>
  );
}

function CodeKey({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="mx-0.5 inline-flex items-center justify-center rounded-sm border border-ink/20 bg-paper px-1.5 font-mono text-[10px] text-ink">
      {children}
    </kbd>
  );
}
